import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, Calendar as CalendarIcon, LogOut, ChevronLeft, ChevronRight, RotateCcw, ExternalLink, ShieldCheck, Bell, BellOff, Download, ClipboardCheck, Check, X, Volume2, VolumeX } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "seo_ezeefit_token";
const LAST_SEEN_KEY = "seo_ezeefit_last_seen";
const LAST_APPROVAL_COUNT_KEY = "seo_ezeefit_last_approval_count";
const SOUND_ENABLED_KEY = "seo_ezeefit_sound_enabled";
const APPROVAL_POLL_MS = 45000; // check for new approvals every 45s

// Tiny data-URL beep (WAV, ~0.3s, 800Hz) — plays when a new approval arrives
// so the owner hears it even if the browser tab is in the background.
const BEEP_DATA_URL = "data:audio/wav;base64,UklGRnQFAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YVAFAAAAAP4Y+jDrRvNVzGGdaKZq6mgYYzZaGFCXRJI36ihWGKMHVfjc6cbcRdG4x1zAWLuxuHK4nrsywerFuNJz4qb0Sgj7HGwyGkjOWFxjSGpEbERoIWL0WAdOI0MzNv0nyxc0BwT4mund2xTRuMcxwPy6r7iBuJm7q8FoxifTt+MB9roJyR5EM2ZJAlrIY4tqrmoUaBph31eZTHNBBjTOJUAV6QSk9V/oL9tK0PDG9r/OuvW3Y7jvux/CBseF0/nkPvUOCyoglTP1Sf5aOWRVayZrnGdEZ1lWA0tzP7cxWSNVEmMCV/N+56jaRc9FxsK+wLo9uHK4YrxywtLGYNS656/2ohw2Kf88+jHlRvpVsGHjbLdqx2gVYytaU08sRIA2cyi+F4EGRfhx6TjcaNH8x1zAdrsCuHK4vLvywqvILtRr5UD3fBHXHqwzHUpJW4pkw2thawtnRl4LVGVIdz1RMD8hCg==";

function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Format ISO date as YYYY-MM-DD in local time
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch (_e) { return ""; }
}

// ── LOGIN ────────────────────────────────────────────────────────────
function LoginScreen({ onSuccess }) {
  // step: 1=credentials, 2=OTP after login, 3=forgot email, 4=forgot OTP+new password
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [tempToken, setTempToken] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const submitCreds = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.requires_2fa) {
        setTempToken(res.data.temp_token);
        setMaskedEmail(res.data.masked_email);
        setStep(2);
        toast.success(`OTP sent to ${res.data.masked_email}`);
      } else if (res.data.token) {
        localStorage.setItem(TOKEN_KEY, res.data.token);
        onSuccess();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // remember=true → backend mints a 30-day JWT so the installed PWA
      // opens tap-and-go for the next month before OTP is needed again.
      const res = await axios.post(`${API}/auth/2fa/verify`, { temp_token: tempToken, otp_code: otp, remember: true });
      if (res.data.token) {
        localStorage.setItem(TOKEN_KEY, res.data.token);
        onSuccess();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const sendForgotOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("If this email is registered, a 6-digit code has been sent.");
      setOtp("");
      setNewPassword("");
      setStep(4);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitForgotReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password-with-otp`, {
        email,
        otp_code: otp,
        new_password: newPassword,
      });
      toast.success("Password reset. Please sign in with your new password.");
      setStep(1);
      setPassword("");
      setOtp("");
      setNewPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. Check the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center px-6 text-white">
      <img src="/pwa-icon-192.png" alt="Ezeefit SEO Monitor App" className="w-20 h-20 rounded-full mb-4 shadow-lg" />
      <h1 className="text-2xl font-bold tracking-tight text-center">Ezeefit SEO Monitor App</h1>
      <p className="text-sm text-slate-400 mb-8">Owner-only SEO monitor</p>
      <form
        onSubmit={
          step === 1 ? submitCreds
          : step === 2 ? submitOtp
          : step === 3 ? sendForgotOtp
          : submitForgotReset
        }
        className="w-full max-w-sm space-y-3"
      >
        {step === 1 && (
          <>
            <input
              type="email"
              required
              placeholder="Admin email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 focus:border-[#F97316] outline-none text-base"
              data-testid="login-email"
            />
            <input
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 focus:border-[#F97316] outline-none text-base"
              data-testid="login-password"
            />
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-xs text-slate-400 text-center">Enter the 6-digit code sent to <strong>{maskedEmail}</strong></p>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength="6"
              required
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 focus:border-[#F97316] outline-none text-base text-center tracking-widest text-xl font-mono"
              data-testid="login-otp"
              autoFocus
            />
            <p className="text-[10px] text-emerald-400 text-center flex items-center justify-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              This device will stay signed in for 30 days — tap-and-open, no OTP needed.
            </p>
          </>
        )}

        {step === 3 && (
          <>
            <p className="text-xs text-slate-400 text-center">Enter your admin email — we'll send a 6-digit reset code.</p>
            <input
              type="email"
              required
              placeholder="Admin email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 focus:border-[#F97316] outline-none text-base"
              data-testid="forgot-email"
              autoFocus
            />
          </>
        )}

        {step === 4 && (
          <>
            <p className="text-xs text-slate-400 text-center">Code sent to <strong>{email}</strong>. Enter it below along with a new password.</p>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength="6"
              required
              placeholder="6-digit code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 focus:border-[#F97316] outline-none text-base text-center tracking-widest text-xl font-mono"
              data-testid="forgot-otp"
              autoFocus
            />
            <input
              type="password"
              required
              minLength={8}
              placeholder="New password (8+ chars, letter, number, special)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 focus:border-[#F97316] outline-none text-base"
              data-testid="forgot-new-password"
            />
          </>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-lg bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 font-semibold flex items-center justify-center gap-2 transition-colors"
          data-testid="login-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" />
            : step === 1 ? "Continue"
            : step === 2 ? "Verify & Sign In"
            : step === 3 ? "Send reset code"
            : "Reset password"}
        </button>

        <div className="pt-2 text-center">
          {step === 1 && (
            <button
              type="button"
              onClick={() => { setStep(3); setPassword(""); }}
              className="text-xs text-[#F97316] hover:text-orange-300 underline"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          )}
          {(step === 3 || step === 4) && (
            <button
              type="button"
              onClick={() => { setStep(1); setOtp(""); setNewPassword(""); }}
              className="text-xs text-slate-400 hover:text-white"
              data-testid="back-to-login-link"
            >
              ← Back to sign in
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

// ── CALENDAR VIEW ─────────────────────────────────────────────────────
function CalendarView({ onDaySelect }) {
  // Freeze `today` at mount so the useMemo below has a stable dependency
  // (a fresh `new Date()` on every render was tripping react-hooks/exhaustive-deps).
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() + 1 });
  const [activity, setActivity] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/seo/monitor/activity`, {
        params: { year: cursor.year, month: cursor.month },
        headers: authHeaders(),
      });
      const map = {};
      (r.data.days || []).forEach((d) => { map[d.date] = d.count; });
      setActivity(map);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        window.location.reload();
      } else {
        toast.error("Failed to load activity");
      }
    } finally {
      setLoading(false);
    }
  }, [cursor.year, cursor.month]);

  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => {
    const first = new Date(cursor.year, cursor.month - 1, 1);
    const daysInMonth = new Date(cursor.year, cursor.month, 0).getDate();
    const startOffset = first.getDay(); // 0=Sunday
    const arr = [];
    for (let i = 0; i < startOffset; i += 1) arr.push({ blank: true });
    for (let d = 1; d <= daysInMonth; d += 1) {
      const iso = toISODate(new Date(cursor.year, cursor.month - 1, d));
      arr.push({ day: d, iso, count: activity[iso] || 0, isToday: iso === toISODate(today) });
    }
    return arr;
  }, [cursor, activity, today]);

  const monthName = new Date(cursor.year, cursor.month - 1, 1).toLocaleString([], { month: "long", year: "numeric" });
  const totalMonth = Object.values(activity).reduce((a, b) => a + b, 0);

  const shift = (delta) => {
    let y = cursor.year;
    let m = cursor.month + delta;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setCursor({ year: y, month: m });
  };

  const dotColor = (n) => {
    if (n === 0) return "bg-slate-700";
    if (n <= 5) return "bg-emerald-500";
    if (n <= 15) return "bg-amber-500";
    return "bg-rose-500";
  };

  return (
    <div className="p-4 pb-24">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => shift(-1)} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" data-testid="cal-prev">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <div className="text-lg font-semibold" data-testid="cal-month-label">{monthName}</div>
          <div className="text-xs text-slate-400" data-testid="cal-total">{loading ? "Loading…" : `${totalMonth} edits this month`}</div>
        </div>
        <button onClick={() => shift(1)} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" data-testid="cal-next">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-slate-500 mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => c.blank ? (
            <div key={i} className="aspect-square" />
          ) : (
            <button
              key={i}
              onClick={() => c.count > 0 && onDaySelect(c.iso)}
              disabled={c.count === 0}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center text-xs relative transition-colors ${
                c.isToday ? "ring-2 ring-[#F97316]" : ""
              } ${c.count > 0 ? "bg-slate-800 hover:bg-slate-700 cursor-pointer" : "bg-slate-900 opacity-50"}`}
              data-testid={`cal-day-${c.iso}`}
            >
              <span className="font-medium">{c.day}</span>
              {c.count > 0 && (
                <span className={`mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white ${dotColor(c.count)}`}>{c.count}</span>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="mt-5 flex items-center gap-3 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full" /> 1-5</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded-full" /> 6-15</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-rose-500 rounded-full" /> 16+</span>
      </div>
    </div>
  );
}

// ── DAY DETAIL VIEW ───────────────────────────────────────────────────
function DayDetail({ date, onBack }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rollingBack, setRollingBack] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/seo/monitor/day`, { params: { date }, headers: authHeaders() });
      setEntries(r.data || []);
    } catch (err) {
      toast.error("Failed to load day");
    } finally {
      setLoading(false);
    }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const rollback = async (e) => {
    if (!window.confirm(`Roll back this edit on ${e.resource_id}?\nThe previous value will be restored.`)) return;
    setRollingBack(e.id);
    try {
      await axios.post(`${API}/admin/seo/audit/${e.id}/rollback`, {}, { headers: authHeaders() });
      toast.success("Rolled back — refresh the affected page to see the change");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rollback failed");
    } finally {
      setRollingBack(null);
    }
  };

  const humanDate = new Date(`${date}T00:00:00`).toLocaleDateString([], { weekday: "long", day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="p-4 pb-24">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="p-2 rounded-lg bg-slate-800" data-testid="day-back">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="text-lg font-semibold" data-testid="day-title">{humanDate}</div>
          <div className="text-xs text-slate-400" data-testid="day-count">{entries.length} edit{entries.length === 1 ? "" : "s"}</div>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">No edits on this day.</div>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <div key={e.id} className="bg-slate-800 rounded-xl p-3 border border-slate-700" data-testid={`entry-${e.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-400">{fmtTime(e.timestamp)} · {e.editor?.email || "unknown"}</div>
                  <div className="font-mono text-xs text-slate-300 break-all mt-0.5">{e.resource_id}</div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded bg-slate-700 text-slate-300 capitalize whitespace-nowrap">{(e.resource_type || "").replace("_", " ")}</span>
              </div>
              {e.changed_fields?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {e.changed_fields.map((f) => (
                    <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F97316]/20 text-[#F97316]">{f}</span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                {e.page_url ? (
                  <a
                    href={`${window.location.origin}${e.page_url}?_seo_preview=${Date.now()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-xs flex items-center justify-center gap-1"
                    data-testid={`preview-${e.id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Preview
                  </a>
                ) : (
                  <div
                    className="flex-1 py-2 rounded-lg bg-slate-800 text-slate-500 text-xs flex items-center justify-center gap-1 cursor-not-allowed"
                    title="This edit has no associated page URL (e.g. a global image alt change)"
                    data-testid={`preview-disabled-${e.id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> No page
                  </div>
                )}
                <button
                  onClick={() => rollback(e)}
                  disabled={rollingBack === e.id || e.resource_type === "redirect"}
                  className="flex-1 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-xs flex items-center justify-center gap-1 font-medium"
                  data-testid={`rollback-${e.id}`}
                  title={e.resource_type === "redirect" ? "Redirects are not roll-back-able (per owner policy)" : "Restore previous value"}
                >
                  {rollingBack === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Roll back
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── APPROVALS VIEW ────────────────────────────────────────────────────
function ApprovalsView({ onBack, onDecided }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(null); // { id, action }
  // Keep the latest onDecided in a ref so `load` doesn't need it as a dep
  // (otherwise the parent-prop's identity changes every render and we loop forever).
  const onDecidedRef = useRef(onDecided);
  useEffect(() => { onDecidedRef.current = onDecided; }, [onDecided]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/seo/monitor/pending-approvals`, { headers: authHeaders() });
      setItems(r.data?.items || []);
      localStorage.setItem(LAST_APPROVAL_COUNT_KEY, String(r.data?.count ?? 0));
      onDecidedRef.current?.(r.data?.count ?? 0);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        window.location.reload();
      } else {
        toast.error("Failed to load approvals");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (item, decision) => {
    if (decision === "reject" && !window.confirm(`Reject Speicher's proposed edit for ${item.page_path}?`)) return;
    setDeciding({ id: item.id, action: decision });
    try {
      await axios.post(
        `${API}/admin/seo/monitor/approvals/${item.kind}/${item.id}/decide`,
        { decision },
        { headers: authHeaders() },
      );
      toast.success(decision === "approve" ? "Approved and applied" : "Rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Decision failed");
    } finally {
      setDeciding(null);
    }
  };

  const _fieldDiff = (item) => {
    // Build a readable list of field-by-field before/after diffs for display
    if (item.kind === "content_request") {
      return [{ field: "text", before: item.current?.text || "", after: item.proposed?.text || "" }];
    }
    const cur = item.current || {};
    const prop = item.proposed || {};
    const keys = Array.from(new Set([...Object.keys(cur), ...Object.keys(prop)])).filter(
      (k) => !["updated_at", "updated_by", "path", "id"].includes(k),
    );
    return keys
      .map((k) => ({ field: k, before: cur[k] || "", after: prop[k] || "" }))
      .filter((d) => d.before !== d.after);
  };

  return (
    <div className="p-4 pb-24">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="p-2 rounded-lg bg-slate-800" data-testid="approvals-back">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="text-lg font-semibold">Approvals</div>
          <div className="text-xs text-slate-400" data-testid="approvals-count">{loading ? "Loading…" : `${items.length} pending`}</div>
        </div>
        {items.length === 0 && !loading && <ShieldCheck className="w-5 h-5 text-emerald-400" />}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">All caught up — no pending approvals.</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const diffs = _fieldDiff(item);
            const isBusy = deciding?.id === item.id;
            return (
              <div key={item.id} className="bg-slate-800 rounded-xl p-3 border border-slate-700" data-testid={`approval-${item.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-400">{fmtTime(item.submitted_at)} · {item.editor_email}</div>
                    <div className="font-mono text-xs text-slate-300 break-all mt-0.5">{item.page_path}</div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 capitalize whitespace-nowrap">
                    {item.kind === "content_request" ? "content" : "meta/schema"}
                  </span>
                </div>
                {item.notes && (
                  <div className="mb-2 text-xs text-slate-400 italic">&ldquo;{item.notes.slice(0, 200)}&rdquo;</div>
                )}
                <div className="space-y-2 mb-3">
                  {diffs.slice(0, 4).map((d, i) => (
                    <div key={i} className="text-[11px] bg-slate-900 rounded p-2">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{d.field}</div>
                      <div className="text-rose-300 line-through opacity-70 break-words">{String(d.before).slice(0, 200) || "(empty)"}</div>
                      <div className="text-emerald-300 break-words mt-1">{String(d.after).slice(0, 200)}</div>
                    </div>
                  ))}
                  {diffs.length > 4 && (
                    <div className="text-[10px] text-slate-500 text-center">+ {diffs.length - 4} more field(s)</div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => decide(item, "reject")}
                    disabled={isBusy}
                    className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-xs flex items-center justify-center gap-1 font-medium"
                    data-testid={`reject-${item.id}`}
                  >
                    {isBusy && deciding.action === "reject" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                    Reject
                  </button>
                  <button
                    onClick={() => decide(item, "approve")}
                    disabled={isBusy}
                    className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-xs flex items-center justify-center gap-1 font-medium"
                    data-testid={`approve-${item.id}`}
                  >
                    {isBusy && deciding.action === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    Approve
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── SHELL: header + install prompt + notifications ────────────────────
function AppShell({ onLogout, pendingCount, onOpenApprovals, soundEnabled, toggleSound, children }) {
  const [notifEnabled, setNotifEnabled] = useState(
    typeof Notification !== "undefined" && Notification.permission === "granted",
  );
  const [installEvt, setInstallEvt] = useState(null);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/seo-monitor-sw.js", { scope: "/seo-monitor" }).catch(() => undefined);
    }
    const handler = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const enableNotifs = async () => {
    if (typeof Notification === "undefined") { toast.error("Notifications not supported on this device"); return; }
    const p = await Notification.requestPermission();
    if (p === "granted") { setNotifEnabled(true); toast.success("Notifications enabled"); }
    else toast.error("Notifications blocked");
  };

  const triggerInstall = async () => {
    if (!installEvt) { toast.info("Use browser menu → 'Add to Home Screen'"); return; }
    installEvt.prompt();
    const choice = await installEvt.userChoice;
    if (choice.outcome === "accepted") { toast.success("App installed"); setInstallEvt(null); }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <header className="sticky top-0 z-10 bg-[#0F172A]/95 backdrop-blur border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/pwa-icon-192.png" alt="" className="w-7 h-7 rounded-full" />
          <div>
            <div className="font-bold text-sm">Ezeefit SEO Monitor</div>
            <div className="text-[10px] text-slate-400 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Owner monitor</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onOpenApprovals}
            className="relative p-2 rounded-lg hover:bg-slate-800"
            title={pendingCount > 0 ? `${pendingCount} pending approval${pendingCount === 1 ? "" : "s"}` : "No pending approvals"}
            data-testid="btn-approvals"
          >
            <ClipboardCheck className={`w-4 h-4 ${pendingCount > 0 ? "text-amber-400" : "text-slate-400"}`} />
            {pendingCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-[9px] font-bold flex items-center justify-center" data-testid="approvals-badge">
                {pendingCount > 9 ? "9+" : pendingCount}
              </span>
            )}
          </button>
          <button onClick={toggleSound} className="p-2 rounded-lg hover:bg-slate-800" title={soundEnabled ? "Sound alerts on" : "Sound alerts off"} data-testid="btn-sound">
            {soundEnabled ? <Volume2 className="w-4 h-4 text-emerald-400" /> : <VolumeX className="w-4 h-4 text-slate-500" />}
          </button>
          {installEvt && (
            <button onClick={triggerInstall} className="p-2 rounded-lg text-[#F97316] hover:bg-slate-800" title="Install as app" data-testid="btn-install">
              <Download className="w-4 h-4" />
            </button>
          )}
          <button onClick={enableNotifs} className="p-2 rounded-lg hover:bg-slate-800" title={notifEnabled ? "Browser notifications on" : "Enable browser notifications"} data-testid="btn-notif">
            {notifEnabled ? <Bell className="w-4 h-4 text-emerald-400" /> : <BellOff className="w-4 h-4 text-slate-500" />}
          </button>
          <button onClick={onLogout} className="p-2 rounded-lg hover:bg-slate-800" title="Logout" data-testid="btn-logout">
            <LogOut className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </header>
      {children}
    </div>
  );
}

// ── ROOT PAGE ─────────────────────────────────────────────────────────
export default function SEOMonitorPWA() {
  const [authed, setAuthed] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));
  const [view, setView] = useState("calendar"); // 'calendar' | 'day' | 'approvals'
  const [selectedDate, setSelectedDate] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(
    localStorage.getItem(SOUND_ENABLED_KEY) !== "false",
  );

  useEffect(() => {
    document.title = "Ezeefit SEO Monitor App";
    // Inject PWA manifest link so this page is installable
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }
    const prevHref = link.href;
    link.href = "/pwa-manifest.json";
    // Apple touch icon
    let apple = document.querySelector('link[rel="apple-touch-icon"]');
    const prevApple = apple ? apple.href : null;
    if (!apple) { apple = document.createElement("link"); apple.rel = "apple-touch-icon"; document.head.appendChild(apple); }
    apple.href = "/pwa-icon-180.png";
    // Theme color
    let meta = document.querySelector('meta[name="theme-color"]');
    const prevMeta = meta ? meta.content : null;
    if (!meta) { meta = document.createElement("meta"); meta.name = "theme-color"; document.head.appendChild(meta); }
    meta.content = "#0F172A";
    return () => {
      if (prevHref) link.href = prevHref;
      if (prevApple) apple.href = prevApple; else if (apple.parentNode) apple.parentNode.removeChild(apple);
      if (prevMeta !== null) meta.content = prevMeta;
    };
  }, []);

  // Poll pending-approvals every 45s while authed. Play a sound + browser notification
  // when the count INCREASES (i.e. Speicher just submitted a new gated edit that needs
  // owner approval). Skips the initial load so we don't beep on app-open.
  useEffect(() => {
    if (!authed) return undefined;
    let cancelled = false;
    let audio = null;
    try { audio = new Audio(BEEP_DATA_URL); } catch (_e) { audio = null; }

    const tick = async () => {
      try {
        const r = await axios.get(`${API}/admin/seo/monitor/pending-approvals`, { headers: authHeaders() });
        if (cancelled) return;
        const newCount = r.data?.count ?? 0;
        const lastSeenRaw = Number(localStorage.getItem(LAST_APPROVAL_COUNT_KEY) ?? "-1");
        setPendingCount(newCount);
        if (lastSeenRaw >= 0 && newCount > lastSeenRaw) {
          // NEW pending items arrived — beep + toast + browser notification
          if (soundEnabled && audio) {
            audio.currentTime = 0;
            audio.play().catch(() => undefined);
          }
          toast.info(`${newCount - lastSeenRaw} new approval${newCount - lastSeenRaw === 1 ? "" : "s"} pending`);
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
              new Notification("Ezeefit SEO Monitor", {
                body: `${newCount - lastSeenRaw} new gated-page edit${newCount - lastSeenRaw === 1 ? "" : "s"} waiting for your approval`,
                icon: "/pwa-icon-192.png",
                tag: "seo-approval",
                requireInteraction: true,
              });
            } catch (_e) { /* silent */ }
          }
        }
        localStorage.setItem(LAST_APPROVAL_COUNT_KEY, String(newCount));
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          if (!cancelled) setAuthed(false);
        }
      }
    };
    tick();
    const iv = setInterval(tick, APPROVAL_POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [authed, soundEnabled]);

  const toggleSound = () => {
    setSoundEnabled((s) => {
      const next = !s;
      localStorage.setItem(SOUND_ENABLED_KEY, String(next));
      toast.success(next ? "Sound alerts on" : "Sound alerts off");
      return next;
    });
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (!authed) return (<><LoginScreen onSuccess={() => setAuthed(true)} /><Toaster position="top-center" theme="dark" /></>);

  return (
    <AppShell
      onLogout={logout}
      pendingCount={pendingCount}
      onOpenApprovals={() => setView("approvals")}
      soundEnabled={soundEnabled}
      toggleSound={toggleSound}
    >
      {view === "approvals" ? (
        <ApprovalsView
          onBack={() => setView("calendar")}
          onDecided={(newCount) => setPendingCount(newCount)}
        />
      ) : selectedDate ? (
        <DayDetail date={selectedDate} onBack={() => setSelectedDate(null)} />
      ) : (
        <CalendarView onDaySelect={(d) => { setSelectedDate(d); setView("day"); }} />
      )}
      <Toaster position="top-center" theme="dark" />
    </AppShell>
  );
}
